import { useEffect, useState } from 'react';
import { ENV_CONFIG } from 'config/environment';
import { useAppSelector } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import { useNavigate } from 'react-router-dom';
import { getAdminsState, getSelectedRecipientId } from 'selectors/admins';
import {
  useCheckOnboardingStatusQuery,
  usePatchAdminUserMutation,
  useGetAdminsRecipientsQuery,
} from 'services/adminsApi';
import { getIsRecipient } from 'config/permissions';
import Icon, { IconNames } from 'components/Icon';
import colors from 'constants/colors';

export const Welcome = () => {
  const navigate = useNavigate();
  const [tosAccepted, setTosAccepted] = useState(true);
  const { selectedRecipient, activeRole } = useAppSelector(getAdminsState);
  const recipientId = useAppSelector(getSelectedRecipientId);
  const [error, setError] = useState('');

  // Fetch recipients using RTK Query - ensures they're loaded before navigating to dashboard
  useGetAdminsRecipientsQuery(undefined, {
    skip: !activeRole || !getIsRecipient(activeRole),
  });

  // Fetch OBCheck data using RTK Query
  const {
    data: OBCheck,
    isLoading,
    refetch: refetchOBCheck,
  } = useCheckOnboardingStatusQuery(recipientId || '', {
    skip: !recipientId,
  });

  // Patch admin user mutation
  const [patchAdminUser, { isLoading: isUpdating }] = usePatchAdminUserMutation();

  // If TOS is already accepted, navigate to dashboard
  useEffect(() => {
    if (OBCheck && OBCheck.is_tos_accepted === true) {
      navigate('/dashboard', { replace: true });
    }
  }, [OBCheck, navigate]);

  const toggleTosAccepted = () => {
    setTosAccepted(tosAccepted => !tosAccepted);
  };

  const onContinue = async () => {
    if (!selectedRecipient) {
      return;
    }
    try {
      await patchAdminUser({ tos_accepted: new Date().toISOString() }).unwrap();
      trackEvent('Terms Accepted', { screen: 'stripe_onboarding' });
      // Refetch OBCheck to confirm TOS is accepted
      const { data: updatedOBCheck } = await refetchOBCheck();
      // Navigate to dashboard if TOS is confirmed as accepted
      if (updatedOBCheck?.is_tos_accepted === true) {
        navigate('/dashboard', { replace: true });
      }
    } catch {
      setError('Failed to update TOS accepted');
    }
  };

  return (
    <div className="screen-loader">
      <div className="screen-loader-bar">
        <img
          src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/oath_logos/Oath_FullLockup_Color_DarkText.svg`}
          alt="Oath logo"
        />
      </div>
      <div className="stripe-welcome-container">
        <div className="stripe-welcome-content">
          <div className="h1-dtm text-center">Welcome to Oath!</div>
          <div className="stripe-welcome-subheader">
            Oath is a giving platform that connects donors to candidates and organizations where
            their dollars can make a difference
          </div>
          <div className="stripe-welcome-list-container">
            <div className="stripe-welcome-list-item body-text">
              <Icon name={IconNames.CheckCircle} color={colors.white} size={30} />
              <span>
                All donations through Oath are direct from the donor to your campaign or
                organization
              </span>
            </div>
            <div className="stripe-welcome-list-item body-text">
              <Icon name={IconNames.CheckCircle} color={colors.white} size={30} />
              <span>
                Campaigns & organizations will receive weekly payouts & donor data to meet reporting
                requirements
              </span>
            </div>
            <div className="stripe-welcome-list-item body-text">
              <Icon name={IconNames.CheckCircle} color={colors.white} size={30} />
              <span>
                Donor information is shared for compliance purposes only and cannot be used to
                contact donors
              </span>
            </div>
          </div>
          <div className="stripe-welcome-tos-checkbox-container">
            <div className="form-check form-check-inline">
              <label htmlFor="checkbox-tos" className="form-check-label">
                <input
                  id="checkbox-tos"
                  type="checkbox"
                  value="tos_accepted"
                  checked={tosAccepted}
                  onChange={toggleTosAccepted}
                />
                <span className="form-check-sign"></span>
              </label>
            </div>

            <span className="utility-text text-white">
              By continuing, you accept Oath&apos;s{' '}
              <a
                href={`${ENV_CONFIG().CLOUD_STORAGE.URL}/legal/Oath Recipient Agreement.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                terms of service.
              </a>
            </span>
          </div>
          <div className="stripe-welcome-button-container">
            <button
              className="btn-inkBlue-inverted row submit-button btn margin0"
              disabled={!tosAccepted || !OBCheck || !selectedRecipient || isLoading || isUpdating}
              onClick={onContinue}
            >
              {isLoading || isUpdating ? 'Loading...' : 'Continue'}
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    </div>
  );
};
